import React, { useEffect, useRef, useState } from 'react';
import { X, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import type { BDAction } from '../../pages/BDApprovals';

interface Props {
  action: BDAction;
  label: string;
  onClose: () => void;
  onRejected: () => void;
}

const MAX_REASON = 500;

/**
 * BD-03. Rejecting a proposal, with an optional reason — replaces the browser
 * prompt. The reason is stored on the action (reject_reason) and shown in the
 * audit history, so it says why the assistant should not have proposed this.
 * Rejecting writes nothing anywhere else.
 */
const ActionRejectModal: React.FC<Props> = ({ action, label, onClose, onRejected }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const quote = action.evidence?.[0]?.quote;

  useEffect(() => {
    textareaRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const reject = async () => {
    setSaving(true);
    try {
      await api.post(`/api/bd-approvals/${action.id}/reject`, { reason: reason.trim() || undefined });
      toast.success('Rejected');
      onRejected();
    } catch (err) {
      toast.error('Failed to reject');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="bd-reject-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Reject proposal</h2>
          <button onClick={onClose} disabled={saving} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-md bg-gray-50 border px-3 py-2">
            <div className="text-sm font-medium text-gray-900">{label}</div>
            {quote && <div className="text-xs text-gray-500 mt-0.5 italic">"{quote}"</div>}
          </div>
          <div>
            <label htmlFor="bd-reject-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="bd-reject-reason"
              ref={textareaRef}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON))}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !saving) reject(); }}
              rows={3}
              placeholder="e.g. Not a client — internal vendor"
              data-testid="bd-reject-reason"
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="text-xs text-gray-400 text-right">{reason.length}/{MAX_REASON}</div>
          </div>
          <p className="text-xs text-gray-500">Nothing is written to Zoho, Outlook or Jarvis. The reason is kept in the approval history.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm rounded-md border text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={reject}
            disabled={saving}
            data-testid="bd-reject-confirm"
            className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <XCircle className="w-4 h-4" /> {saving ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionRejectModal;
